import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const WORKWEEKS = {
  weekdays: { label: 'Monday–Friday', days: [1,2,3,4,5] },
  sixday: { label: 'Monday–Saturday', days: [1,2,3,4,5,6] },
  daily: { label: 'Every day', days: [0,1,2,3,4,5,6] },
};

const FAQ = [
  ['How does the install schedule planner calculate the finish date?', 'It starts on the date you enter and counts only the workdays included in the workweek you choose. Crew workdays and contingency workdays are both counted as working days.'],
  ['Does the planner account for weather automatically?', 'No. Weather, permitting, inspection delays, material availability and customer access are not predicted. Add contingency workdays if you want a buffer in the planning window.'],
  ['What should I use for installation days?', 'Use the whole scheduled workdays from your own labor plan or another duration you have estimated for the project. PicketOps does not choose the productivity assumption for you.'],
  ['Does this update a PicketOps job schedule?', 'No. This public planner creates a planning window only. Signed-in contractors can open Jobs & install planning to save an actual install date and install-day count.'],
];

const parseLocalDate = value => value ? new Date(value + 'T12:00:00') : null;
const isoDate = date => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth()+1).padStart(2,'0');
  const day = String(date.getDate()).padStart(2,'0');
  return year + '-' + month + '-' + day;
};
const formatDate = date => date
  ? date.toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'})
  : '—';

function addWorkdays(start, workdays, allowedDays) {
  if (!start || workdays < 1) return null;
  const date = new Date(start);
  let counted = 0;
  while (counted < workdays) {
    if (allowedDays.includes(date.getDay())) counted += 1;
    if (counted < workdays) date.setDate(date.getDate()+1);
  }
  return date;
}

function nextWorkingDay(start, allowedDays) {
  if (!start) return null;
  const date = new Date(start);
  for (let i=0;i<7;i+=1) {
    if (allowedDays.includes(date.getDay())) return date;
    date.setDate(date.getDate()+1);
  }
  return date;
}

export default function FenceInstallSchedulePlanner() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [project,setProject] = useState(searchParams.get('project') || '');
  const [startDate,setStartDate] = useState('');
  const [installDays,setInstallDays] = useState(searchParams.get('days') || '');
  const [bufferDays,setBufferDays] = useState('0');
  const [workweek,setWorkweek] = useState('weekdays');
  const [copied,setCopied] = useState(false);

  const result = useMemo(()=>{
    const start = parseLocalDate(startDate);
    const install = Number(installDays);
    const buffer = Number(bufferDays);
    const allowed = WORKWEEKS[workweek].days;
    const valid = Boolean(start)
      && Number.isInteger(install) && install > 0
      && Number.isInteger(buffer) && buffer >= 0;

    if(!valid) return {valid:false,start:null,finish:null,crewFinish:null,totalWorkdays:0,calendarDays:0};

    const effectiveStart = nextWorkingDay(start,allowed);
    const crewFinish = addWorkdays(effectiveStart,install,allowed);
    const totalWorkdays = install + buffer;
    const finish = addWorkdays(effectiveStart,totalWorkdays,allowed);
    const calendarDays = Math.round((finish-effectiveStart)/(1000*60*60*24))+1;
    return {valid:true,start:effectiveStart,crewFinish,finish,totalWorkdays,calendarDays};
  },[startDate,installDays,bufferDays,workweek]);

  const scheduleText=useMemo(()=>{
    if(!result.valid) return '';
    return [
      'PicketOps Fence Installation Schedule Plan',
      project.trim() ? 'Project: '+project.trim() : null,
      'Workweek: '+WORKWEEKS[workweek].label,
      'Requested start: '+startDate,
      result.start && isoDate(result.start)!==startDate ? 'First available working day: '+isoDate(result.start) : null,
      'Crew installation workdays: '+Number(installDays),
      'Contingency workdays: '+Number(bufferDays),
      '',
      'PLANNED WINDOW',
      'Crew work begins: '+formatDate(result.start),
      'Crew production finishes: '+formatDate(result.crewFinish),
      Number(bufferDays)>0 ? 'Buffered planning finish: '+formatDate(result.finish) : null,
      'Total planned working days: '+result.totalWorkdays,
      'Calendar span: '+result.calendarDays+' days',
      '',
      'Planning window only. Confirm crew availability, weather, materials, permits, inspections, access and customer commitments before promising dates.',
    ].filter(Boolean).join('\n');
  },[result,project,workweek,startDate,installDays,bufferDays]);

  const copySchedule=async()=>{
    if(!scheduleText)return;
    try{
      await navigator.clipboard.writeText(scheduleText);
      setCopied(true);
      window.setTimeout(()=>setCopied(false),1800);
    }catch{
      setCopied(false);
    }
  };

  const clear=()=>{
    setProject('');
    setStartDate('');
    setInstallDays(searchParams.get('days')||'');
    setBufferDays('0');
    setWorkweek('weekdays');
    setCopied(false);
  };

  const hasInput = startDate || String(installDays).trim()!=='' || bufferDays!=='0';
  const workspaceTarget=isAuthenticated?'/jobs':'/contractor/signup';

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span><span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span><span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span></span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link><span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link><span aria-hidden="true"> / </span>
          <span aria-current="page">Fence installation schedule planner</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-hero overflow-hidden rounded-3xl border border-primary/20 shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free install schedule planner · No signup required</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">Turn crew workdays into a realistic fence installation window</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">Choose a start date, workweek and contingency buffer to see the crew-production finish and the buffered planning finish before you commit the job schedule.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><a href="#schedule-planner">Plan install window <ApperIcon name="ArrowDown" size={16}/></a></Button>
                <Button variant="outline" asChild><Link to="/fence-labor-crew-planner">Calculate crew-days first</Link></Button>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-card/60 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Keep the promise separate from the plan</p>
              <div className="mt-5 space-y-3">
                {[
                  ['CalendarDays','Start date','The date you want the planning window to begin.'],
                  ['BriefcaseBusiness','Workweek','Monday–Friday, Monday–Saturday or every day.'],
                  ['HardHat','Crew workdays','The whole install days you already estimated.'],
                  ['ShieldPlus','Contingency','Optional working-day buffer you control.'],
                ].map(([icon,title,detail])=><div key={title} className="flex gap-3 rounded-lg border border-border bg-background p-3"><span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name={icon} size={16}/></span><div><p className="text-sm font-semibold">{title}</p><p className="mt-0.5 text-xs text-muted-foreground">{detail}</p></div></div>)}
              </div>
            </div>
          </div>
        </section>

        <section id="schedule-planner" aria-labelledby="schedule-heading" className="grid gap-5 scroll-mt-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div><p className="text-xs font-semibold uppercase tracking-wide text-primary">Schedule assumptions</p><h2 id="schedule-heading" className="mt-2 text-2xl font-heading font-semibold">Build the install window from your actual work pattern.</h2></div>
              <div className="space-y-2"><Label htmlFor="schedule-project">Project label <span className="text-muted-foreground">(optional)</span></Label><Input id="schedule-project" value={project} onChange={event=>setProject(event.target.value)} placeholder="Smith privacy fence"/></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="schedule-start">Requested start date</Label><Input id="schedule-start" type="date" value={startDate} onChange={event=>setStartDate(event.target.value)}/></div>
                <div className="space-y-2"><Label htmlFor="schedule-install-days">Crew installation workdays</Label><Input id="schedule-install-days" type="number" min="1" step="1" inputMode="numeric" value={installDays} onChange={event=>setInstallDays(event.target.value)} placeholder="3"/></div>
                <div className="space-y-2"><Label htmlFor="schedule-buffer">Contingency workdays</Label><Input id="schedule-buffer" type="number" min="0" step="1" inputMode="numeric" value={bufferDays} onChange={event=>setBufferDays(event.target.value)} placeholder="0"/></div>
                <div className="space-y-2"><Label htmlFor="schedule-workweek">Working days</Label><Select value={workweek} onValueChange={setWorkweek}><SelectTrigger id="schedule-workweek"><SelectValue/></SelectTrigger><SelectContent>{Object.entries(WORKWEEKS).map(([value,entry])=><SelectItem key={value} value={value}>{entry.label}</SelectItem>)}</SelectContent></Select></div>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">If the requested start falls on a non-working day, the planner moves the working start to the next permitted day. It does not account for holidays automatically.</p>
              {hasInput&&!result.valid&&<p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">Choose a start date, enter a positive whole number of crew workdays, and use a whole nonnegative number for contingency days.</p>}
              <Button type="button" variant="ghost" onClick={clear}><ApperIcon name="RotateCcw" size={16}/>Clear planner</Button>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <section className="overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-sm)">
              <div className="border-b border-border bg-primary/5 p-5 sm:p-6">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">Planned installation window</p>
                <h2 className="mt-2 text-2xl font-heading font-semibold">{result.valid?'Schedule window ready to review':'Complete the scheduling assumptions'}</h2>
              </div>
              <dl className="grid gap-px bg-border sm:grid-cols-2">
                {[
                  ['Crew starts',result.valid?formatDate(result.start):'—','First working day in the selected workweek'],
                  ['Crew production finish',result.valid?formatDate(result.crewFinish):'—','Last crew-production workday'],
                  ['Buffered finish',result.valid?formatDate(result.finish):'—','Includes entered contingency workdays'],
                  ['Calendar span',result.valid?result.calendarDays+' days':'—',result.valid?result.totalWorkdays+' planned working days':'Crew days + contingency'],
                ].map(([label,value,detail])=><div key={label} className="bg-card p-5"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 text-xl font-bold">{value}</dd><p className="mt-1 text-xs text-muted-foreground">{detail}</p></div>)}
              </dl>
            </section>

            {result.valid&&(
              <section className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-start gap-3"><span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"><ApperIcon name="CalendarCheck2" size={19}/></span><div><p className="text-sm font-semibold">Before committing the date</p><p className="mt-1 text-sm leading-relaxed text-muted-foreground">Confirm the crew, materials, access, permits/inspections, customer availability and weather exposure. The buffered finish is a planning aid, not a promised completion date.</p></div></div>
              </section>
            )}
          </div>
        </section>

        <div className="flex flex-wrap gap-3">
          <Button type="button" onClick={copySchedule} disabled={!result.valid}><ApperIcon name={copied?'Check':'Copy'} size={16}/>{copied?'Copied':'Copy schedule plan'}</Button>
          {result.valid?<Button variant="outline" asChild><a href={'data:text/plain;charset=utf-8,'+encodeURIComponent(scheduleText)} download="picketops-fence-install-schedule.txt"><ApperIcon name="Download" size={16}/>Download schedule plan</a></Button>:<Button variant="outline" disabled><ApperIcon name="Download" size={16}/>Download schedule plan</Button>}
          <Button variant="outline" asChild><Link to={workspaceTarget}>{isAuthenticated?'Open Jobs & install planning':'Create account'} <ApperIcon name="ArrowRight" size={16}/></Link></Button>
        </div>

        <section aria-labelledby="schedule-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="schedule-faq-heading" className="text-2xl font-heading font-semibold">Fence installation scheduling questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">{FAQ.map(([question,answer])=><details key={question} className="py-4"><summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary><p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p></details>)}</div>
        </section>
      </main>
    </div>
  );
}

export const route={path:'/fence-install-schedule-planner',layout:'public'};
